import React, { useEffect, useState } from 'react'
import { ActionIcon, Box, Button, Divider, Group, LoadingOverlay, MantineProvider, Menu, Stack, Table, Text, TextInput, Title } from '@mantine/core'
import { useHistory } from 'react-router'
import { usePricingVersions } from '../../hooks/global-job-matching'
import { IconCopy, IconDotsVertical, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import { getShortDate } from '../../lib/formatters'
import { useDisclosure } from '@mantine/hooks'
import { PricingVersion } from '../../components/GlobalJobMatching/GlobalJobMatchingVersioning/types/versions'
import { CreateNewVersionModal, DeleteVersionModal, RenameVersionModal, VersionListTableHead } from '../../components/GlobalJobMatching/PricingVersionsList'
import { StatusIcon } from '../../components/GlobalJobMatching/PricingVersionStepper/SideProgress/StatusIcon'
import { globalJobMatchingTheme } from '../../lib/mantineTheme'

const PricingVersionsList = () => {
  const { data: pricingVersionsList, isLoading } = usePricingVersions()
  const [newVersionModalOpen, { open: openCreateNew, close: closeCreateNew }] = useDisclosure(false)
  const [renameVersionModalOpen, { open: openRename, close: closeRename }] = useDisclosure(false)
  const [deleteVersionModalOpen, { open: openDeleteVersion, close: closeDeleteVersion }] = useDisclosure(false)
  const [selectedVersion, setSelectedVersion] = useState<PricingVersion | undefined>()
  const [sort, setSort] = useState<{ field: string, dir: 'asc' | 'desc', isDate: boolean }>({ field: 'name', dir: 'asc', isDate: false })
  const [search, setSearch] = useState('')
  const history = useHistory()
  const maxVersionsReached = pricingVersionsList && pricingVersionsList.length >= 25 && window.location.hostname.includes('marketpay.payscale.com')

  const handleOptionSelect = (option: 'clone' | 'rename' | 'delete', targetVersion: PricingVersion) => {
    setSelectedVersion(targetVersion)
    if (option === 'clone') openCreateNew()
    if (option === 'rename') openRename()
    if (option === 'delete') openDeleteVersion()
  }

  const sortRows = (rows: any[]) => {
    let sortedRows = []

    if (!sort.isDate) sortedRows = rows.sort((a, b) => {
      const fieldA = a[sort.field] || ''
      const fieldB = b[sort.field] || ''
      if (sort.dir === 'asc') {
        return fieldA.localeCompare(fieldB)
      } else {
        return fieldB.localeCompare(fieldA)
      }
    });

    if (sort.isDate) sortedRows = rows.sort((a, b) => {
      if (!a || !b) return 0
      const dateA = new Date(a[sort.field]).getTime()
      const dateB = new Date(b[sort.field]).getTime()

      return sort.dir === 'asc' ? (dateA - dateB) : (dateB - dateA)
    })

    return sortedRows
  }

  const applySearch = (version: PricingVersion) => {
    if (!search?.trim().length) return version
    return version.name.toLowerCase().includes(search?.toLowerCase())
  }


  const handleSort = (field: string, isDate?: boolean) => {
    if (field === sort.field) return setSort({ field, dir: sort.dir === 'asc' ? 'desc' : 'asc', isDate: isDate ?? false })
    return setSort({ field, dir: 'asc', isDate: isDate ?? false })
  }

  useEffect(() => {
    if (!newVersionModalOpen && !deleteVersionModalOpen) setSelectedVersion(undefined)
  }, [newVersionModalOpen, deleteVersionModalOpen])

  const rows = sortRows(pricingVersionsList || []).filter(applySearch).map((version) => (
    <Table.Tr key={version.id} style={{ cursor: 'pointer' }} onClick={() => history.push(`strategy-based-pricing/${version.id}`)}>
      <Table.Td pl={24} fw={500}>{version.name}</Table.Td>
      <Table.Td>{getShortDate(version.lastModified)}</Table.Td>
      <Table.Td>{getShortDate(version.created)}</Table.Td>
      <Table.Td>{version.createdBy}</Table.Td>
      <Table.Td><StatusIcon size='xl' pricingVersion={version} /></Table.Td>
      <Table.Td pr={24} onClick={e => e.stopPropagation()}>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon variant='transparent'>
              <IconDotsVertical height={16} width={16} color='#667085'  />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item c='newBlue' disabled={maxVersionsReached} onClick={() => handleOptionSelect('clone', version)} leftSection={<IconCopy size={16} />}>
              <Text c='newBlue' fw={500}>Clone</Text>
            </Menu.Item>
            <Menu.Item c='newBlue' fw={500} onClick={() => handleOptionSelect('rename', version)} leftSection={<IconEdit size={16} />}>
              <Text c='newBlue'>Rename</Text>
            </Menu.Item>
            <Menu.Item c='red' onClick={() => handleOptionSelect('delete', version)} leftSection={<IconTrash size={16} />}>
              <Text c='red' fw={500}>Delete</Text>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <MantineProvider theme={globalJobMatchingTheme}>
      <Stack h='calc(100vh - 10px)' w='100%'>
        <CreateNewVersionModal opened={newVersionModalOpen} onClose={closeCreateNew} cloneVersion={selectedVersion} />
        { selectedVersion && <RenameVersionModal opened={renameVersionModalOpen} onClose={closeRename} versionId={selectedVersion.id} /> }
        <DeleteVersionModal opened={deleteVersionModalOpen} onClose={closeDeleteVersion} selectedVersion={selectedVersion} />
        <Title mt='lg' ml='lg'>Price at Scale</Title>
        <Divider size='sm' />
        <Group px='lg' justify='space-between'>
          <Title order={2}>Versions</Title>
          <Group>
            <TextInput w={500} placeholder='Search by version name' value={search} onChange={e => setSearch(e.currentTarget.value)} />
            <Button
              size='md'
              disabled={maxVersionsReached}
              leftSection={<IconPlus size={20} />}
              onClick={openCreateNew}
            >
              Add new version
            </Button>
          </Group>
        </Group>
        { maxVersionsReached && <Text size='lg'>You've reached your maximum amount of allowed versions.</Text> }
        <Box h='calc(100vh - 160px)' style={{ overflow: 'scroll' }}>
          <Table h='auto' stickyHeader>
            <LoadingOverlay visible={isLoading} loaderProps={{ size: 100 }} />
            <Table.Thead>
              <Table.Tr>
                <VersionListTableHead field='name' label='Version name' sort={sort} onClick={(field) => handleSort(field)} />
                <VersionListTableHead field='lastModified' label='Last modified' sort={sort} onClick={(field) => handleSort(field, true)} />
                <VersionListTableHead field='created' label='Created on' sort={sort} onClick={(field) => handleSort(field, true)} />
                <VersionListTableHead field='createdBy' label='Created by' sort={sort} onClick={(field) => handleSort(field)} />
                <VersionListTableHead field='runStatus' label='Status' sort={sort} onClick={(field) => handleSort(field)} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </MantineProvider>
  )
}

export default PricingVersionsList
